import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import { AnswerPaths, Paths, QuestionnairePaths, AuthPaths } from '@constants/paths';
import { LettersPage } from '@components/Pages/LettersPage';
import { SubmissionContent } from '@components/Pages/LettersPage/SubmissionContent';
import { PrivateRoute } from '@sharedComponents/hoc/PrivateRoute';
import { AnswerChecker } from '@components/Pages/AnswersPage/Answer/AnswerChecker';
import { AnswerContainer } from '@components/Pages/AnswersPage/Answer';
import { AnswersPage } from '@components/Pages/AnswersPage';
import { QuestionnairePage } from '@components/Pages/QuestionnairePage';
import { Questionnaire } from '@components/Pages/QuestionnairePage/Questionnaire';
import { NotFound } from '@sharedComponents/NotFound';
import { DashboardPage } from '@components/Pages/DashboardPage';
import { RouteChecker } from '@components/Routes/RouteChecker';
import { PermissionManagementPage } from '@components/Pages/DashboardPage/PermissionManagementPage';
import { ChangelogPage } from '@components/Pages/ChangelogPage';
import { PrivatePermissionRoute } from '@sharedComponents/hoc/PrivatePermissionRoute';
import { FeaturePermission, PagePermission, PermissionActions } from '@enums';
import { RolesPage } from '@components/Pages/DashboardPage/RolesPage';
import { QuestionnairesPage } from '@components/Pages/DashboardPage/QuestionnairesPage';
import { CreateRolePage } from '@components/Pages/DashboardPage/RolesPage/CreateRolePage';
import { EditRolePage } from '@components/Pages/DashboardPage/RolesPage/EditRolePage';
import { AuthRoute } from '@sharedComponents/hoc/AuthRoute';
import { LoginRedirect } from '@components/Auth/LoginRedirect';
import { LoginRedirectCallback } from '@components/Auth/LoginRedirectCallback';
import { LoginRefreshCallback } from '@components/Auth/LoginRefreshCallback';
import { LogoutRedirectCallback } from '@components/Auth/LogoutRedirectCallback';
import { LogoutRedirect } from '@components/Auth/LogoutRedirect';
import { LoginPage } from '@components/Auth/LoginPage';
import { App } from '@components/App';

export const AuthRoutes = () => (
  <Routes>
    <Route
      key="auth/redirect"
      path={`${Paths.AUTH}/${AuthPaths.REDIRECT}`}
      element={
        <AuthRoute>
          <LoginRedirect></LoginRedirect>
        </AuthRoute>
      }
    ></Route>
    <Route
      key="auth/login"
      path={`${Paths.AUTH}/${AuthPaths.LOGIN}`}
      element={
        <AuthRoute>
          <LoginRedirectCallback></LoginRedirectCallback>
        </AuthRoute>
      }
    ></Route>

    <Route
      key="auth/refresh"
      path={`${Paths.AUTH}/${AuthPaths.REFRESH}`}
      element={<LoginRefreshCallback></LoginRefreshCallback>}
    ></Route>
    <Route
      key="auth/logout"
      path={`${Paths.AUTH}/${AuthPaths.LOGOUT}`}
      element={<LogoutRedirectCallback></LogoutRedirectCallback>}
    ></Route>
    <Route
      key="auth/logout-redirect"
      path={`${Paths.AUTH}/${AuthPaths.LOGOUT_REDIRECT}`}
      element={<LogoutRedirect></LogoutRedirect>}
    ></Route>
  </Routes>
);

export const AppRoutes = () => {
  return (
    <Routes>
      <Route
        key="auth/test-login"
        path={`${Paths.AUTH}/${AuthPaths.TEST_LOGIN}`}
        element={
          <AuthRoute>
            <LoginPage></LoginPage>
          </AuthRoute>
        }
      ></Route>
      <Route
        key="questionnaire"
        path={Paths.QUESTIONNAIRE}
        element={
          <PrivateRoute>
            <QuestionnairePage />
          </PrivateRoute>
        }
      >
        <Route
          path={QuestionnairePaths.ID}
          element={
            <PrivateRoute>
              <Questionnaire />
            </PrivateRoute>
          }
        />
        <Route
          path={QuestionnairePaths.NOT_FOUND}
          element={
            <PrivateRoute>
              <NotFound />
            </PrivateRoute>
          }
        />
      </Route>
      <Route
        key="answer"
        path={AnswerPaths.ANSWER}
        element={
          <PrivateRoute>
            <AnswerChecker />
          </PrivateRoute>
        }
      >
        <Route path={AnswerPaths.ID} element={<AnswerContainer />} />
      </Route>
      <Route
        path={AnswerPaths.ANSWERS}
        element={
          <PrivatePermissionRoute
            permissionPages={[PagePermission.Answers]}
            permissionActions={[PermissionActions.View]}
          >
            <AnswersPage />
          </PrivatePermissionRoute>
        }
      />
      <Route
        path={Paths.CHANGELOG}
        element={
          <PrivatePermissionRoute
            permissionPages={[PagePermission.Changelog]}
            permissionActions={[PermissionActions.View]}
          >
            <ChangelogPage />
          </PrivatePermissionRoute>
        }
      />
      <Route
        path={Paths.LETTERS}
        element={
          <PrivatePermissionRoute
            permissionPages={[PagePermission.Letters]}
            permissionActions={[PermissionActions.View]}
          >
            <LettersPage />
          </PrivatePermissionRoute>
        }
      >
        <Route path={Paths.ID} element={<SubmissionContent />} />
      </Route>
      <Route
        path={PagePermission.Questionnaires}
        element={
          <PrivatePermissionRoute
            permissionPages={[PagePermission.Questionnaires]}
            permissionActions={[PermissionActions.View]}
          >
            <QuestionnairesPage />
          </PrivatePermissionRoute>
        }
      />
      <Route
        path={PagePermission.Dashboard}
        element={
          <PrivatePermissionRoute
            permissionPages={[
              PagePermission.Questionnaires,
              PagePermission.Management,
              PagePermission.Roles,
            ]}
            permissionActions={[PermissionActions.View]}
          >
            <DashboardPage />
          </PrivatePermissionRoute>
        }
      >
        <Route
          path={PagePermission.Management}
          element={
            <PrivatePermissionRoute
              permissionPages={[PagePermission.Management]}
              permissionActions={[PermissionActions.View]}
            >
              <PermissionManagementPage />
            </PrivatePermissionRoute>
          }
        />
        <Route path={PagePermission.Roles} element={<Outlet />}>
          <Route
            path={''}
            element={
              <PrivatePermissionRoute
                permissionPages={[PagePermission.Roles]}
                permissionActions={[PermissionActions.View]}
              >
                <RolesPage />
              </PrivatePermissionRoute>
            }
          />
          <Route
            path={'create'}
            element={
              <PrivatePermissionRoute
                permissionPages={[PagePermission.Roles]}
                permissionFeatures={[FeaturePermission.RoleItem]}
                permissionActions={[PermissionActions.View]}
                actionFeatures={[PermissionActions.Create]}
              >
                <CreateRolePage />
              </PrivatePermissionRoute>
            }
          />
          <Route
            path={':id/edit'}
            element={
              <PrivatePermissionRoute
                permissionPages={[PagePermission.Roles]}
                permissionActions={[PermissionActions.View]}
                permissionFeatures={[FeaturePermission.RoleItem]}
                actionFeatures={[PermissionActions.Update]}
              >
                <EditRolePage />
              </PrivatePermissionRoute>
            }
          />
        </Route>
      </Route>

      <Route
        path={Paths.NOT_FOUND}
        element={
          <PrivateRoute>
            <NotFound />
          </PrivateRoute>
        }
      />
      <Route path="/*" element={<RouteChecker />} />
    </Routes>
  );
};

export const appRouter = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<App />}>
      <Route path="/*" element={<RouteChecker />} />
    </Route>
  )
);